import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useCallback, useContext, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useCreateMutation } from "../../../api/genericHooks";
import { BookContext } from "../../../contexts/BookContext";
import { covers } from "../../../images";
import { Step } from "../GenerateBookPDFModal";
import { GenPDFStepper } from "./GenPDFStepper";

type Props = {
  open: boolean;
  setOpen: (isOpen: boolean) => void;
  steps: Step[];
  nextStep: () => void;
};

export type UpdateCoverTypeParams = {
  cover_type: "cover_1" | "cover_2" | "cover_3";
};

export const GenerateBookPDFModalStep1: FC<Props> = ({
  open,
  setOpen,
  steps,
  nextStep,
}) => {
  const { t } = useTranslation();
  const { book, refresh } = useContext(BookContext);

  // Form Methods =============================================================
  const GeneratePDF1FormSchema = Yup.object().shape({
    cover_type: Yup.mixed<"cover_1" | "cover_2" | "cover_3">()
      .required(t("errors.required"))
      .oneOf(["cover_1", "cover_2", "cover_3"], t("errors.required")),
  });

  const defaultValues = useMemo<UpdateCoverTypeParams>(() => {
    return { cover_type: book?.cover_type || "cover_1" };
  }, [book]);

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<UpdateCoverTypeParams>({
    defaultValues: defaultValues,
    resolver: yupResolver(GeneratePDF1FormSchema),
  });

  // Mutations ===============================================================
  const updateCoverTypeMutation = useCreateMutation(
    "/update_cover_type",
    {},
    false,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );

  // Form Submit =============================================================
  const onSubmit = useCallback(
    (values: UpdateCoverTypeParams) => {
      updateCoverTypeMutation.mutateAsync({ book: values }).then(() => {
        toast.success(t("generateBookPDFModal.coverTypeUpdated"));
        refresh();
        nextStep();
      });
    },
    [refresh, t, nextStep, updateCoverTypeMutation]
  );

  const selectedCoverType = watch("cover_type");

  if (!book) {
    return null;
  }

  return (
    <Dialog open={open} onClose={setOpen} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-2 sm:p-4 text-center sm:items-center">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white text-slate-800 px-3 sm:px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-4xl sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95 w-full mx-2 sm:mx-0"
          >
            <form
              className="bg-white flex flex-1 flex-col justify-between"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="absolute right-0 top-0 pr-3 pt-3 sm:pr-4 sm:pt-4 block">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded bg-white text-slate-400 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-delicate-500 focus:ring-offset-2"
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon
                    aria-hidden="true"
                    className="w-5 h-5 sm:w-6 sm:h-6"
                  />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-left sm:ml-4 sm:mt-0 w-full">
                  <DialogTitle
                    as="h3"
                    className="text-xl sm:text-2xl font-semibold text-slate-800 pr-8 sm:pr-0"
                  >
                    {t("generateBookPDFModal.title")}
                  </DialogTitle>
                  <hr className="mt-2 w-full border-gray-200" />

                  <GenPDFStepper steps={steps} />

                  <p className="mt-4 text-sm sm:text-base text-slate-700 font-semibold">
                    {t("generateBookPDFModal.chooseCoverType")}
                  </p>
                  <div
                    className="mt-2 text-xs sm:text-sm text-slate-700"
                    dangerouslySetInnerHTML={{
                      __html: t(
                        "generateBookPDFModal.chooseCoverTypeDescription",
                        {
                          interpolation: {
                            escapeValue: false,
                          },
                        }
                      ),
                    }}
                  />

                  <div className="flex flex-col sm:flex-row mt-5 space-y-3 sm:space-y-0 sm:space-x-3 justify-around mx-auto items-center">
                    <div
                      className={[
                        selectedCoverType === "cover_1"
                          ? "border-herb-700"
                          : "border-gray-200",
                        "w-fit p-4 border-4 rounded h-fit",
                      ].join(" ")}
                    >
                      <img
                        src={covers.cover1}
                        alt="Cover 1"
                        className="w-32 sm:w-40 h-auto cursor-pointer"
                        onClick={() => setValue("cover_type", "cover_1")}
                      />
                    </div>
                    {/* <div
                      className={[
                        selectedCoverType === "cover_2"
                          ? "border-herb-700"
                          : "border-gray-200",
                        "w-fit p-4 border-4 rounded h-fit",
                      ].join(" ")}
                    >
                      <img
                        src={covers.cover2}
                        alt="Cover 2"
                        className="w-40 h-auto cursor-pointer"
                        onClick={() => setValue("cover_type", "cover_2")}
                      />
                    </div>
                    <div
                      className={[
                        selectedCoverType === "cover_3"
                          ? "border-herb-700"
                          : "border-gray-200",
                        "w-fit p-4 border-4 rounded h-fit",
                      ].join(" ")}
                    >
                      <img
                        src={covers.cover3}
                        alt="Cover 3"
                        className="w-40 h-auto cursor-pointer"
                        onClick={() => setValue("cover_type", "cover_3")}
                      />
                    </div> */}
                    {errors.cover_type && (
                      <span className="text-rust-400 font-nunito">
                        {errors.cover_type.message}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 flex flex-col sm:flex-row-reverse gap-3 sm:gap-0">
                <button
                  type="submit"
                  className="inline-flex w-full justify-center rounded bg-rust-50 px-4 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-rust-300 hover:bg-rust-100 sm:w-auto"
                >
                  {t("addPicturesModal.save")}
                </button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
